import { useEffect, useRef, useState } from 'react'
import { playgroundUrl } from './playgroundUrl'

interface SvgViewerProps {
    src: string
    alt?: string
    label?: string
    className?: string
    maxHeight?: string
    /** EN source to link to playground */
    enSource?: string
    /** Optional trailing slot rendered at the right edge of the label bar — used for caller-specific controls (e.g. theme toggle) so they sit inside the frame chrome rather than above it. */
    topbarTrailing?: React.ReactNode
}

export function SvgViewer({ src, alt = 'Diagram', label, className = '', maxHeight = '500px', enSource, topbarTrailing }: SvgViewerProps) {
    const [scale, setScale] = useState(1)
    const [pan, setPan] = useState({ x: 0, y: 0 })
    const isZoomed = scale !== 1 || pan.x !== 0 || pan.y !== 0

    const rootRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [isFullscreen, setIsFullscreen] = useState(false)

    // Native fullscreen API — the outermost viewer container is what enters
    // fullscreen, so label/footer/zoom controls come along. The browser
    // handles Esc / F11 cleanly via the fullscreenchange event, which we
    // listen for to keep the local isFullscreen state in sync.
    const toggleFullscreen = () => {
        const el = rootRef.current
        if (!el) return
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {})
        } else {
            el.requestFullscreen().catch(() => {})
        }
    }
    useEffect(() => {
        const onChange = () => {
            const fs = !!document.fullscreenElement
            setIsFullscreen(fs)
            // When the user exits fullscreen, reset the viewer to its
            // default fit-view state. Any zoom/pan they did inside
            // fullscreen was situational for that larger canvas and
            // would look misaligned once the viewer snaps back to its
            // 520px-tall card.
            if (!fs) {
                setScale(1)
                setPan({ x: 0, y: 0 })
            }
        }
        document.addEventListener('fullscreenchange', onChange)
        return () => document.removeEventListener('fullscreenchange', onChange)
    }, [])
    const pinchRef = useRef({ dist: 0, baseScale: 1, midX: 0, midY: 0, panX: 0, panY: 0 })
    const dragRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 })

    const reset = () => { setScale(1); setPan({ x: 0, y: 0 }) }
    const zoomIn = () => setScale(s => Math.min(10, s * 1.25))
    const zoomOut = () => setScale(s => {
        const next = s / 1.25
        if (next <= 1) { setPan({ x: 0, y: 0 }); return 1 }
        return next
    })

    // Desktop zoom via Ctrl/Cmd + wheel only — a bare scroll wheel passes
    // through to page scroll so the user can scroll past the image. On Mac
    // trackpad pinch, macOS also fires wheel events with ctrlKey=true, so
    // this path handles both. Exponential + clamped deltaY so a single
    // mouse-wheel tick (deltaY ~100) gives a strong response without
    // screen-jumping, and a tiny trackpad delta (deltaY ~5) still moves
    // visibly per frame instead of needing five pinches to see anything.
    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        const onWheel = (e: WheelEvent) => {
            if (!(e.ctrlKey || e.metaKey)) return
            e.preventDefault()
            const d = Math.max(-100, Math.min(100, e.deltaY))
            setScale(s => Math.max(1, s * Math.exp(-d * 0.01)))
        }
        el.addEventListener('wheel', onWheel, { passive: false })
        return () => el.removeEventListener('wheel', onWheel)
    }, [])

    const getTouchDist = (touches: React.TouchList) => {
        const dx = touches[0].clientX - touches[1].clientX
        const dy = touches[0].clientY - touches[1].clientY
        return Math.sqrt(dx * dx + dy * dy)
    }
    const getTouchMid = (touches: React.TouchList) => ({
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
    })

    // Two-finger pinch = zoom. Single-finger drag = pan, but only when already
    // zoomed in — at scale=1 a single finger falls through to page scroll
    // (`touchAction: pan-y`). That way idle images don't hijack the page.
    const onTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 2) {
            e.preventDefault()
            const dist = getTouchDist(e.touches)
            const mid = getTouchMid(e.touches)
            pinchRef.current = { dist, baseScale: scale, midX: mid.x, midY: mid.y, panX: pan.x, panY: pan.y }
        } else if (e.touches.length === 1 && isZoomed) {
            const t = e.touches[0]
            dragRef.current = { x: t.clientX, y: t.clientY, panX: pan.x, panY: pan.y }
        }
    }

    const onTouchMove = (e: React.TouchEvent) => {
        if (e.touches.length === 2) {
            e.preventDefault()
            const p = pinchRef.current
            const newDist = getTouchDist(e.touches)
            const newScale = Math.max(1, p.baseScale * (newDist / p.dist))
            setScale(newScale)
            const mid = getTouchMid(e.touches)
            setPan({ x: p.panX + (mid.x - p.midX), y: p.panY + (mid.y - p.midY) })
        } else if (e.touches.length === 1 && isZoomed) {
            e.preventDefault()
            const t = e.touches[0]
            const d = dragRef.current
            setPan({ x: d.panX + (t.clientX - d.x), y: d.panY + (t.clientY - d.y) })
        }
    }

    // When a pinch ends with one finger still on the screen, reinit
    // dragRef against that finger's current position. Otherwise the
    // stale dragRef (still pointing at the previous single-finger
    // session's start, or untouched memory) makes the first follow-up
    // touchmove register a massive delta — the image "throws" away.
    const onTouchEnd = (e: React.TouchEvent) => {
        if (e.touches.length === 1) {
            const t = e.touches[0]
            dragRef.current = { x: t.clientX, y: t.clientY, panX: pan.x, panY: pan.y }
        }
    }

    // Mouse drag = pan when zoomed. At scale=1 the image is inert so the
    // browser's default drag behavior (text selection, image drag) is fine.
    const onMouseDown = (e: React.MouseEvent) => {
        if (e.button !== 0 || !isZoomed) return
        e.preventDefault()
        const startX = e.clientX
        const startY = e.clientY
        const startPan = { x: pan.x, y: pan.y }
        const onMove = (ev: MouseEvent) => {
            setPan({ x: startPan.x + (ev.clientX - startX), y: startPan.y + (ev.clientY - startY) })
        }
        const onUp = () => {
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
        }
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
    }

    return (
        <div
            ref={rootRef}
            className={`rounded-xl overflow-hidden flex flex-col relative ${className}`}
            // Fixed height — maxHeight is treated as a target for the whole
            // component so siblings (e.g. a code panel next to the viewer)
            // can align to the exact same total height. Label + footer bars
            // shrink to content; the image container flexes to fill. When
            // the element enters native fullscreen the height prop is
            // dropped so the viewer fills the screen rather than sitting
            // at its layout height in the corner.
            style={{
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                height: isFullscreen ? '100%' : maxHeight,
            }}
        >
            {(label || topbarTrailing) && (
                <div
                    className="px-4 py-2 bg-surface-elevated text-[11px] font-mono text-text-muted flex-shrink-0 flex items-center justify-between gap-3"
                    style={{ borderBottom: '1px solid var(--border-subtle)' }}
                >
                    <span>{label}</span>
                    {topbarTrailing}
                </div>
            )}
            <div
                ref={containerRef}
                className="overflow-hidden relative flex-1 min-h-0"
                // When zoomed, block browser gestures so our pan handlers own
                // the pointer. When at rest, `pan-y` lets vertical page scroll
                // pass through (mobile) and single-pointer drag does nothing
                // (desktop) — the image is inert until the user opts in.
                style={{
                    touchAction: isZoomed ? 'none' : 'pan-y',
                    cursor: isZoomed ? 'grab' : 'default',
                    userSelect: isZoomed ? 'none' : 'auto',
                }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onTouchCancel={onTouchEnd}
                onMouseDown={onMouseDown}
                onDoubleClick={reset}
            >
                <div
                    className="w-full h-full flex items-center justify-center p-4"
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                        transformOrigin: 'center center',
                    }}
                >
                    {/* <object> loads the SVG as its own sandboxed document
                        (scripts blocked — same XSS surface as <img>) but
                        unlike <img> it resolves the SVG's embedded @import
                        for JetBrains Mono, so text vertical-centring uses
                        the font the renderer calibrated dy=0.35em for. */}
                    <object
                        data={src}
                        type="image/svg+xml"
                        aria-label={alt}
                        className="max-w-full max-h-full pointer-events-none select-none"
                    />
                </div>
                {/* Fullscreen toggle — top-right overlay, mirrors the
                    zoom-controls placement at bottom-right. Uses the
                    native Fullscreen API; Esc exits. Separate stopProp
                    block so the button doesn't trigger the container's
                    pan/dblclick handlers. */}
                <div
                    className="absolute top-3 right-3"
                    onClick={e => e.stopPropagation()}
                    onDoubleClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    <button
                        onClick={toggleFullscreen}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-text-muted hover:text-text transition-colors"
                        style={{
                            background: 'color-mix(in srgb, var(--surface) 88%, transparent)',
                            border: '1px solid var(--border-subtle)',
                            backdropFilter: 'blur(8px)',
                        }}
                        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                        title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
                    >
                        {isFullscreen ? (
                            /* exit: arrows pointing inward */
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                                stroke="currentColor" strokeWidth="1.5"
                                strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 1v3H2M5 11V8H2M7 1v3h3M7 11V8h3" />
                            </svg>
                        ) : (
                            /* enter: arrows pointing outward */
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                                stroke="currentColor" strokeWidth="1.5"
                                strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2 4V2h2M10 4V2h-2M2 8v2h2M10 8v2h-2" />
                            </svg>
                        )}
                    </button>
                </div>
                {/* Zoom controls overlay — trackpad pinch / mouse wheel
                    can still feel fiddly on some machines, so expose
                    explicit buttons as an always-on fallback.

                    stopPropagation on both click and dblclick: without it,
                    two rapid `+` clicks fire a native dblclick event that
                    bubbles to the container's onDoubleClick={reset} and
                    snaps the view back to 100% mid-zoom. onMouseDown also
                    stopped so the pan handler doesn't start a drag from
                    inside this widget when already zoomed. */}
                <div
                    className="absolute bottom-3 right-3 flex items-center gap-0.5 rounded-lg font-mono text-[11px]"
                    style={{
                        background: 'color-mix(in srgb, var(--surface) 88%, transparent)',
                        border: '1px solid var(--border-subtle)',
                        backdropFilter: 'blur(8px)',
                    }}
                    onClick={e => e.stopPropagation()}
                    onDoubleClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    <button
                        onClick={zoomOut}
                        className="w-7 h-7 flex items-center justify-center text-text-muted hover:text-text transition-colors"
                        aria-label="Zoom out"
                        title="Zoom out"
                    >−</button>
                    <button
                        onClick={reset}
                        className="w-7 h-7 flex items-center justify-center text-text-muted hover:text-text transition-colors border-x"
                        style={{ borderColor: 'var(--border-subtle)' }}
                        aria-label="Reset zoom"
                        title={isZoomed ? `Reset to fit (${Math.round(scale * 100)}%)` : 'Fit to view'}
                    >
                        {/* Refresh / reset — circular arrow (Feather rotate-ccw) */}
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2.25"
                            strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="1 4 1 10 7 10" />
                            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                        </svg>
                    </button>
                    <button
                        onClick={zoomIn}
                        className="w-7 h-7 flex items-center justify-center text-text-muted hover:text-text transition-colors"
                        aria-label="Zoom in"
                        title="Zoom in"
                    >+</button>
                </div>
            </div>
            {enSource && (
                <div
                    className="flex items-center justify-between px-4 py-1.5 bg-surface-elevated flex-shrink-0"
                    style={{ borderTop: '1px solid var(--border-subtle)' }}
                >
                    <span className="text-[10px] font-mono text-text-muted/50">
                        Generated with <code className="text-text-muted">render</code> tool
                    </span>
                    <a
                        href={playgroundUrl + '?code=' + encodeURIComponent(enSource)}
                        className="text-[10px] font-mono text-accent hover:underline"
                    >
                        Open in Playground &rarr;
                    </a>
                </div>
            )}
        </div>
    )
}
